import { Content as _Content, Columns } from '#~/common/editor/index.js';
import { Protocol } from '#~/control/components/protocol/index.js';
import { useAnalytics } from '#~/analytics/hooks/index.js';
import { EVENT_CATEGORIES, EVENT_NAMES } from '#~/analytics/constants/index.js';
import { resolveDeviceAnalyticsCatalogContextFromModel } from '#~/analytics/device-context.js';
import {
  createMediaControlsDiscoveryTracker,
  isEmbeddedDiscovery,
  resolveEmbeddedFlowProps
} from '#~/analytics/domains/index.js';
import { useMultiDeviceStateQuery } from '#~/statequery/hook.js';
import type { MultiDeviceSelectorDeviceInstace } from '#~/statequery/types.js';
import { useProducer, useStore } from '../store/hooks.js';
import { Preview } from '../preview/preview.jsx';
import { Controller } from './controller.jsx';
import { Http } from './http.jsx';
import { Onvif } from './onvif.jsx';
import { ViscaIp } from './visca-ip.jsx';
import { ViscaSerial } from './visca-serial.jsx';
import { Callout } from '@studio/design-system/callout';
import { Group, Message } from '@studio/design-system/form';
import { AnimatePresence, motion, Motions } from '@studio/design-system/motion';
import type { HtmlAttributes } from '@studio/design-system/props';
import { Protocol as M_Protocol } from '@studio/models/control';
import { toAccessor } from '@studio/react-utils';
import { useLatestFn } from '@studio/react-utils/hooks';
import { isNil } from '@tryiris-ai/js-types';
import type { T_Maybe } from '@tryiris-ai/js-types';
import type { ComponentType, ReactElement } from 'react';
import { createElement, useEffect, useMemo, useRef } from 'react';
import { shallow } from 'zustand/shallow';
import { StatusDeviceConnectionStatus } from '@tryiris-ai/common.protojs/agent/v1/agent';
import { locateProxy } from '#~/agent/index.js';
import type { Device } from '#~/device/index.js';
import type { Severity } from '@studio/design-system/messaging';

const Settings: Record<M_Protocol, ComponentType<HtmlAttributes>> = {
  [M_Protocol.VISCA_IP]: ViscaIp,
  [M_Protocol.VISCA_SERIAL]: ViscaSerial,
  [M_Protocol.ONVIF]: Onvif,
  [M_Protocol.HTTP]: Http
};

export const ConnectionStatus = ({
  controlStatus,
  deviceId,
  error
}: {
  controlStatus?: StatusDeviceConnectionStatus | undefined;
  deviceId?: T_Maybe<string>;
  error?: unknown;
}): ReactElement | null => {
  if (!deviceId) {
    return (
      <Callout severity='info' title='Connecting'>
        <Callout.P>{''}</Callout.P>
      </Callout>
    );
  }

  if (error) {
    return (
      <Callout severity='error' title='Connection failed'>
        <Callout.P>An error occurred connecting your controls</Callout.P>
      </Callout>
    );
  }

  const getConnectionInfo = (): { title: string; text: string; severity: Severity } => {
    switch (controlStatus) {
      case StatusDeviceConnectionStatus.CONNECTION_STATUS_CONNECTED:
        return {
          title: 'Connected',
          text: 'Corresponding controller settings have been set. Please test the camera controls to confirm they are working correctly.',
          severity: 'info'
        };
      case StatusDeviceConnectionStatus.CONNECTION_STATUS_CONNECTING:
        return {
          title: 'Connecting',
          text: '',
          severity: 'info'
        };
      case StatusDeviceConnectionStatus.CONNECTION_STATUS_UNKNOWN:
        return {
          title: 'Connecting',
          text: '',
          severity: 'info'
        };
      case StatusDeviceConnectionStatus.CONNECTION_STATUS_FAILED:
        return {
          title: 'Not responding',
          text: 'The camera is not responding. Please test the camera controls to confirm they are working. You can try different options in Advanced Settings menu.',
          severity: 'warning'
        };
      default:
        return {
          title: 'Connecting',
          text: '',
          severity: 'info'
        };
    }
  };

  const { title, text, severity } = getConnectionInfo();

  return (
    <Callout severity={severity} title={title}>
      <Callout.P>{text}</Callout.P>
    </Callout>
  );
};

/** Control `<Content>` component. */
export const Content = (props: HtmlAttributes): ReactElement => {
  const producer = useProducer();
  const { track } = useAnalytics();
  const mediaControlsDiscoveryTracker = useMemo(
    () => createMediaControlsDiscoveryTracker(track),
    [track]
  );

  // Per ENG-3960 §E12 — read canonical device cohort props outside the gated selector below
  // so select_control_type / select_no_control always receive them, even before candidate.control is set.
  const wizardDeviceCatalogContext = useStore(
    (µ) =>
      resolveDeviceAnalyticsCatalogContextFromModel(
        µ.manufacturer?.config?.model,
        µ.candidate.modelId ?? µ.source.selection?.modelId
      ),
    shallow
  );

  // Per ENG-3960 §1.7 — control-config steps are part of the embedded funnel; stamp
  // the additive is_embedded + linking_surface cohort when the in-flight discovery is
  // embedded ({} otherwise), matching the wizard step emits.
  const sourceSelection = useStore((µ) => µ.source.selection);
  // ENG-4832 C3: the operator typed a camera that is not in the catalogue.
  const isNotListedTyped = useStore((µ) => µ.identification?.identifiedBy === 'not_listed');
  const isEmbeddedFlow = isEmbeddedDiscovery(sourceSelection);
  const embeddedFlowProps = useMemo(
    () => resolveEmbeddedFlowProps(isEmbeddedFlow),
    [isEmbeddedFlow]
  );

  const { deviceAnalyticsCatalogContext, deviceId, error, protocol } =
    useStore(({ candidate, manufacturer, previews, source }) => {
      const { control } = candidate;

      if (!control) {
        return undefined;
      }

      const selectedModel = manufacturer?.config?.model;

      const preview = previews.control;

      return {
        // Per ENG-3960 §E12 — device_brand/device_model are off-convention; use canonical *_name keys.
        deviceAnalyticsCatalogContext: resolveDeviceAnalyticsCatalogContextFromModel(
          selectedModel,
          candidate.modelId ?? source.selection?.modelId
        ),
        deviceId: preview?.deviceId,
        error: preview?.error,
        protocol: candidate.control?.protocol
      };
    }, shallow) ?? {};

  const previousControlStatusRef = useRef<StatusDeviceConnectionStatus | undefined>(undefined);
  const previousControlErrorRef = useRef<string | undefined>(undefined);
  const controlStatusSelector = useMemo(
    () => ({
      minInterval: 500,
      // The transform must return the PER-DEVICE value — multiDeviceStateQuery
      // keys the result map by device id itself. Wrapping the value in
      // `{ [device.id]: ... }` makes `controlStatus` an object that matches no
      // enum case, pinning the callout on "Connecting" forever.
      transform: (device: MultiDeviceSelectorDeviceInstace) =>
        device.id === deviceId ? device.status?.movement?.connectionStatus?.status : undefined
    }),
    [deviceId]
  );
  const controlStatusMap = useMultiDeviceStateQuery(controlStatusSelector);
  const controlStatus = deviceId ? controlStatusMap[deviceId as Device['id']] : undefined;

  useEffect(() => {
    if (!deviceId || !protocol) {
      previousControlStatusRef.current = undefined;
      previousControlErrorRef.current = undefined;
      return;
    }

    const proxy = locateProxy(deviceId as Device['id']);
    const errorMessage = error instanceof Error ? error.message : error ? String(error) : undefined;
    const commonProperties = {
      ...(proxy?.id ? { agent_id: proxy.id } : {}),
      ...(proxy?.embedded !== undefined ? { is_embedded: proxy.embedded } : {}),
      ...embeddedFlowProps
    };

    if (
      controlStatus === StatusDeviceConnectionStatus.CONNECTION_STATUS_CONNECTED &&
      previousControlStatusRef.current !== StatusDeviceConnectionStatus.CONNECTION_STATUS_CONNECTED
    ) {
      mediaControlsDiscoveryTracker.trackControlsConnectSuccess({
        ...commonProperties,
        ...deviceAnalyticsCatalogContext,
        device_id: deviceId,
        media_type: 'controls',
        surface: 'add_device',
        ...(controlStatus !== undefined ? { connection_status: controlStatus } : {})
      });
    }

    const hasNewError = errorMessage && previousControlErrorRef.current !== errorMessage;
    const transitionedToFailed =
      controlStatus === StatusDeviceConnectionStatus.CONNECTION_STATUS_FAILED &&
      previousControlStatusRef.current !== StatusDeviceConnectionStatus.CONNECTION_STATUS_FAILED;

    if (hasNewError || transitionedToFailed) {
      mediaControlsDiscoveryTracker.trackControlsConnectError({
        ...commonProperties,
        ...deviceAnalyticsCatalogContext,
        device_id: deviceId,
        media_type: 'controls',
        surface: 'add_device',
        ...(controlStatus !== undefined ? { connection_status: controlStatus } : {}),
        ...(errorMessage ? { error_message: errorMessage } : {})
      });
    }

    previousControlStatusRef.current = controlStatus;
    previousControlErrorRef.current = errorMessage;
  }, [
    controlStatus,
    deviceAnalyticsCatalogContext,
    deviceId,
    error,
    protocol,
    embeddedFlowProps,
    mediaControlsDiscoveryTracker
  ]);

  const handleProtocolChange = useLatestFn((nextProtocol: T_Maybe<M_Protocol>) => {
    producer(({ candidate, control }) => {
      const { cache } = control;

      // Cache the current control settings.
      if (protocol) {
        cache[protocol] = candidate.control;
      }

      // Restore the saved control settings (if applicable)
      candidate.control = nextProtocol
        ? { ...cache[nextProtocol], protocol: nextProtocol }
        : undefined;

      // Per ENG-3960 §E12 — canonical device cohort props read from outer scope (manufacturer config
      // is invariant during protocol selection; gated selector above would be undefined pre-selection).
      if (nextProtocol) {
        track(EVENT_NAMES.SELECT_CONTROL_TYPE, EVENT_CATEGORIES.VIDEO_SOURCES, {
          controlType: nextProtocol,
          ...wizardDeviceCatalogContext,
          ...embeddedFlowProps
        });
      } else {
        track(EVENT_NAMES.SELECT_NO_CONTROL, EVENT_CATEGORIES.VIDEO_SOURCES, {
          controlType: 'none',
          ...wizardDeviceCatalogContext,
          ...embeddedFlowProps
        });
      }
    });
  });

  return (
    <_Content {...props}>
      <Columns>
        <Columns.Column>
          <h3>Control settings</h3>

          <div>
            <Group
              label='Connection type'
              // ENG-4832 C3: an unlisted camera runs on the generic driver, so
              // every connection type (plus No Control) is on offer. A bare
              // string would render as an error (`Message` defaults to
              // severity 'error'), so this is a hint, not a validation failure.
              {...(isNotListedTyped
                ? {
                    message: (
                      <Message
                        severity='info'
                        icon={false}
                        message='All connection types, plus No Control'
                      />
                    )
                  }
                : {})}
            >
              <Protocol
                placeholder={isNotListedTyped ? 'Choose a connection type' : undefined}
                protocol={toAccessor(protocol, handleProtocolChange)}
              />
            </Group>

            <AnimatePresence>
              {protocol ? (
                <motion.div className='grow' key={protocol} {...Motions.Fade}>
                  {createElement(Settings[protocol], { className: 'flex flex-col gap-16 mt-16' })}
                </motion.div>
              ) : null}
            </AnimatePresence>
          </div>

          {!!protocol && (
            <div className='mt-16'>
              <ConnectionStatus controlStatus={controlStatus} deviceId={deviceId} error={error} />
            </div>
          )}
        </Columns.Column>

        <Columns.Column>
          <div>
            <h3>Video preview</h3>
            <Preview />
          </div>

          {!!protocol && <Controller deviceId={deviceId && isNil(error) ? deviceId : undefined} />}
        </Columns.Column>
      </Columns>
    </_Content>
  );
};
